import { getAuthHeaders, authedFetch } from './authService';

const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try {
    window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } }));
  } catch (e) {
    /* ignore */
  }
};

export type CountryItem = { id: number; name: string; active: boolean };
export type StateItem = { id: number; name: string; country_id: number; active: boolean };
export type CityItem = { id: number; name: string; state_id: number; country_id: number; active: boolean };
export type DistrictItem = { id: number; name: string; state_id: number; country_id: number; active: boolean };

async function handleResponse(res: Response, errorPrefix: string): Promise<any> {
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? (await res.text().catch(() => ''));
    const msg = `${errorPrefix}: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/* ---------------- Countries ---------------- */

export async function getCountriesAll(): Promise<CountryItem[]> {
  const url = `${HOSTNAME}/adminportal/locations/countries`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  const body = await handleResponse(res, 'Failed to fetch countries');
  return body?.data?.countries ?? [];
}

export async function createCountry(payload: { name: string; active?: boolean }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/countries`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to create country');
}

export async function updateCountry(id: number, payload: { name?: string }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/countries/${id}`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to update country');
}

export async function setCountryActive(id: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/countries/${id}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  return handleResponse(res, 'Failed to set country active');
}

/* ---------------- States ---------------- */

export async function getStatesAll(): Promise<StateItem[]> {
  const url = `${HOSTNAME}/adminportal/locations/states`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  const body = await handleResponse(res, 'Failed to fetch states');
  return body?.data?.states ?? [];
}

export async function createState(payload: { name: string; country_id: number; active?: boolean }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/states`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to create state');
}

export async function updateState(id: number, payload: { name?: string; country_id?: number }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/states/${id}`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to update state');
}

export async function setStateActive(id: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/states/${id}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  return handleResponse(res, 'Failed to set state active');
}

/* ---------------- Cities ---------------- */

export async function getCitiesAll(): Promise<CityItem[]> {
  const url = `${HOSTNAME}/adminportal/locations/cities`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  const body = await handleResponse(res, 'Failed to fetch cities');
  return body?.data?.cities ?? [];
}

export async function createCity(payload: { name: string; state_id: number; country_id: number; active?: boolean }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/cities`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to create city');
}

export async function updateCity(id: number, payload: { name?: string; state_id?: number; country_id?: number }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/cities/${id}`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to update city');
}

export async function setCityActive(id: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/cities/${id}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  return handleResponse(res, 'Failed to set city active');
}

/* ---------------- Districts ---------------- */

export async function getDistrictsAll(): Promise<DistrictItem[]> {
  const url = `${HOSTNAME}/adminportal/locations/districts`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  const body = await handleResponse(res, 'Failed to fetch districts');
  return body?.data?.districts ?? [];
}

export async function createDistrict(payload: { name: string; state_id: number; country_id: number; active?: boolean }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/districts`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to create district');
}

export async function updateDistrict(id: number, payload: { name?: string; state_id?: number; country_id?: number }): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/districts/${id}`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  return handleResponse(res, 'Failed to update district');
}

export async function setDistrictActive(id: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/locations/districts/${id}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  return handleResponse(res, 'Failed to set district active');
}
